import React, { useState } from 'react';
import './rush.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { DashboardHero, EditorMock, TMList, Integrations } from './RushDashMocks';

const LogoCloud = () => (
  <div className="logos">
    <div className="rs-container">
      <div className="logos-label">Trusted by localization teams shipping in 100+ languages</div>
      <div className="logos-row">
        <span className="logo-mark" style={{ fontFamily: 'Georgia, serif', fontSize: 22 }}>Northwind</span>
        <span className="logo-mark" style={{ letterSpacing: '0.2em', fontWeight: 600 }}>HELIOS</span>
        <span className="logo-mark" style={{ fontStyle: 'italic', fontWeight: 600 }}>tideline</span>
        <span className="logo-mark"><svg width="18" height="18" viewBox="0 0 18 18"><circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" strokeWidth="2" /></svg>Orbital</span>
        <span className="logo-mark" style={{ fontFamily: 'Inter', fontWeight: 800, letterSpacing: '-0.03em' }}>Klein/Co</span>
        <span className="logo-mark"><svg width="18" height="18" viewBox="0 0 18 18"><rect x="3" y="3" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" transform="rotate(45 9 9)" /></svg>Quanta</span>
        <span className="logo-mark" style={{ fontWeight: 700 }}>Maravel</span>
      </div>
    </div>
  </div>
);

const Bento = () => (
  <section className="section">
    <div className="rs-container">
      <div className="section-head reveal">
        <div className="eyebrow">The dashboard</div>
        <h2>Everything localization needs.<br />On one screen.</h2>
        <p className="section-lead">Rush Studio collapses your project tracker, translation editor, memory bank, file pipeline, and deadline tracker into a single, calm workspace.</p>
      </div>
      <div className="bento stagger">
        <div className="bento-card span-4 tall">
          <div className="feature-icon">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="3" width="14" height="3" rx="1" fill="currentColor" opacity="0.35" /><rect x="2" y="8" width="10" height="3" rx="1" fill="currentColor" opacity="0.6" /><rect x="2" y="13" width="6" height="2" rx="1" fill="currentColor" /></svg>
          </div>
          <h3>Project overview &amp; progress</h3>
          <p>See every active translation — owners, language pairs, deadlines, and percent complete — across teams and clients without opening a single email.</p>
          <div className="bento-visual">
            <div style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-soft)' }}>
              <div className="proj-row head" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 0.6fr' }}>
                <span>Project</span><span>Lang</span><span>Progress</span><span>Status</span>
              </div>
              <div className="proj-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 0.6fr', background: 'var(--bg-card)' }}>
                <span className="proj-name"><span className="file-ic">DOC</span><span>Q4 Update</span></span>
                <span><span className="lang-chip">DE</span> <span className="lang-chip">FR</span></span>
                <span className="proj-progress"><span className="bar"><span style={{ width: '68%' }}></span></span><span className="proj-pct">68%</span></span>
                <span><span className="status-pill live"><span className="pulse"></span>live</span></span>
              </div>
              <div className="proj-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 0.6fr', background: 'var(--bg-card)' }}>
                <span className="proj-name"><span className="file-ic">IDM</span><span>Brand v3</span></span>
                <span><span className="lang-chip">ES</span> <span className="lang-chip">IT</span></span>
                <span className="proj-progress"><span className="bar"><span style={{ width: '92%' }}></span></span><span className="proj-pct">92%</span></span>
                <span><span className="status-pill live"><span className="pulse"></span>live</span></span>
              </div>
              <div className="proj-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 0.6fr', background: 'var(--bg-card)' }}>
                <span className="proj-name"><span className="file-ic">JSO</span><span>Onboarding</span></span>
                <span><span className="lang-chip">+6</span></span>
                <span className="proj-progress"><span className="bar"><span style={{ width: '40%' }}></span></span><span className="proj-pct">40%</span></span>
                <span><span className="status-pill queued"><span className="pulse"></span>queued</span></span>
              </div>
            </div>
          </div>
        </div>

        <div className="bento-card span-2 tall">
          <div className="feature-icon indigo">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 5h12M3 9h12M3 13h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </div>
          <h3>AI + human editor</h3>
          <p>Side-by-side source/target. AI drafts, TM matches, locked glossary terms. Humans always have the last word.</p>
          <div className="bento-visual">
            <div style={{ background: 'var(--bg-soft)', borderRadius: 8, padding: 10, fontSize: 12 }}>
              <div style={{ color: 'var(--ink-4)', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em' }}>EN</div>
              <div style={{ color: 'var(--ink-2)', marginBottom: 8 }}>Welcome to your workspace.</div>
              <div style={{ color: 'var(--accent)', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em' }}>DE · AI</div>
              <div style={{ color: 'var(--ink)', fontWeight: 500 }}>Willkommen in Ihrem Workspace.</div>
            </div>
          </div>
        </div>

        <div className="bento-card span-2">
          <div className="feature-icon green">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" fill="none" /><path d="M9 5v4l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </div>
          <h3>Memory &amp; glossaries</h3>
          <p>Approved phrasing snaps in automatically. Per-client glossaries lock brand voice across every word you ship.</p>
        </div>

        <div className="bento-card span-2">
          <div className="feature-icon yellow">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M5 11a3 3 0 010-6 4 4 0 017.5-1A3.5 3.5 0 1114 13H5z" stroke="currentColor" strokeWidth="1.7" fill="none" /></svg>
          </div>
          <h3>Files in. Files out.</h3>
          <p>DOCX, IDML, XLIFF, JSON — round-tripped with formatting intact. Drive, Dropbox, OneDrive native.</p>
          <div className="bento-visual">
            <Integrations />
          </div>
        </div>

        <div className="bento-card span-2">
          <div className="feature-icon">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="3" y="4" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" fill="none" /><path d="M3 8h12M7 2v3M11 2v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
          </div>
          <h3>Deadlines &amp; assignment</h3>
          <p>Per-language pair owners, automated stage handoffs, slack/email alerts before anything slips.</p>
        </div>
      </div>
    </div>
  </section>
);

const FeatureRows = () => (
  <section className="section" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
    <div className="rs-container">
      <div className="feature-row reveal">
        <div className="feature-text">
          <div className="eyebrow">AI drafts. Human polish.</div>
          <h2 style={{ fontSize: 36 }}>An editor your reviewers will actually use.</h2>
          <p className="section-lead">Side-by-side source/target. AI suggestions next to translation memory matches. Glossary terms locked at the segment so brand voice stays consistent — segment after segment, language after language.</p>
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>3-pane workflow</b> · source / target / context, all visible at once</span></div>
            <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>Inline TM &amp; glossary hints</b> · accept, edit, or reject without leaving the line</span></div>
            <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>Formatting preserved</b> · brackets, bold, code, footnotes round-trip cleanly</span></div>
          </div>
        </div>
        <div className="product-window" style={{ borderRadius: 16 }}>
          <div className="product-chrome">
            <div className="traffic"><span /><span /><span /></div>
            <div className="url">rush.studio/projects/q4/segment/12</div>
          </div>
          <div style={{ padding: 16, background: 'var(--bg-card)' }}><EditorMock /></div>
        </div>
      </div>

      <div className="feature-row reverse reveal">
        <div className="product-window">
          <div className="product-chrome">
            <div className="traffic"><span /><span /><span /></div>
            <div className="url">rush.studio/memory</div>
          </div>
          <div style={{ padding: 20, background: 'var(--bg-card)' }}>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12, fontWeight: 600 }}>Memory matches · "welcome to" · DE</div>
            <TMList />
            <div style={{ marginTop: 16, fontSize: 12, color: 'var(--ink-4)' }}>+ 12 more matches across 4 projects</div>
          </div>
        </div>
        <div className="feature-text">
          <div className="eyebrow">Translation memory</div>
          <h2 style={{ fontSize: 36 }}>Translate it once.<br />Reuse it forever.</h2>
          <p className="section-lead">Every approved sentence becomes searchable, scored, and ready to reuse the next time. Per-client glossaries protect brand voice. The next project starts 40% done.</p>
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>Fuzzy + exact matches</b> · 70/85/95/100% scoring with one click to insert</span></div>
            <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>Per-client glossaries</b> · enforce terminology automatically</span></div>
            <div style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>→</span> <span><b>Brand voice rules</b> · tone, register, do-not-translate lists</span></div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const STEP_ICONS = {
  upload: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 16V4m0 0l-4 4m4-4l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  assign: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 19c.8-3 3.4-4.5 6-4.5s5.2 1.5 6 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 6h5M18.5 3.5v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  translate: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 5h8M7 3v2M5 5c0 4 2 7 6 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11 13c-2 0-4-1-5-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M13 21l4-10 4 10M14.5 17h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  export: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12V6a2 2 0 012-2h7l5 5v7a2 2 0 01-2 2h-3" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M14 4v4a2 2 0 002 2h3" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M3 16h9m0 0l-3-3m3 3l-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const Differentiators = () => {
  const cards = [
    { tint: '', cta: 'Tune your voice', title: 'Brand voice, locked in every language', desc: 'Tone sliders, form of address, and do-not-translate rules the AI actually follows — preview the voice before a single word ships.', icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 7v4M6.5 4.5v9M10 6v6M13.5 4v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>) },
    { tint: 'indigo', cta: 'See automations', title: 'Localization on autopilot', desc: 'Translate on every pull request, auto-route by language, and deliver the moment a project hits 100%. Build pipelines, not tickets.', icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="3" width="6" height="4.5" rx="1.2" stroke="currentColor" strokeWidth="1.7" /><rect x="10" y="10.5" width="6" height="4.5" rx="1.2" stroke="currentColor" strokeWidth="1.7" /><path d="M8 5.2h3a2 2 0 012 2v3.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>) },
    { tint: 'green', cta: 'See AI quality', title: 'Trust the AI, verifiably', desc: 'Every draft gets a quality estimate and risk flags. Auto-approve what\'s safe, and send only the segments that need a human to your team.', icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 13a6 6 0 1112 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><path d="M9 13l3.5-3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>) },
    { tint: 'yellow', cta: 'Open preview', title: 'Catch broken layouts before they ship', desc: 'See translations rendered in the real UI — web, mobile, email — with live warnings the moment a string overflows or clips its container.', icon: (<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 9s2.8-5 7-5 7 5 7 5-2.8 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="1.7" /></svg>) },
  ];
  return (
    <section className="section" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
      <div className="rs-container">
        <div className="section-head reveal">
          <div className="eyebrow">What sets us apart</div>
          <h2>More than a translation tool.</h2>
          <p className="section-lead">Four things you won't find bolted onto a legacy TMS — built in, so your brand stays consistent, your pipeline runs itself, and your launches never break.</p>
        </div>
        <div className="reveal" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(248px, 1fr))', gap: 16, marginTop: 8 }}>
          {cards.map((c) => (
            <div className="bento-card" key={c.title} style={{ display: 'flex', flexDirection: 'column' }}>
              <div className={`feature-icon ${c.tint}`}>{c.icon}</div>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
              <span style={{ marginTop: 'auto', paddingTop: 16, color: 'var(--accent)', fontWeight: 600, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {c.cta}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h9M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => (
  <section className="section">
    <div className="rs-container">
      <div className="section-head reveal">
        <div className="eyebrow">How it works</div>
        <h2>From upload to delivery in four steps.</h2>
      </div>
      <div className="steps stagger">
        <div className="step">
          <div className="step-head">
            <div className="step-icon">{STEP_ICONS.upload}</div>
            <div className="step-num">1</div>
          </div>
          <h4>Upload or sync</h4>
          <p>Drop any file — DOCX, IDML, XLIFF, JSON — or pull straight from Drive, Dropbox, or OneDrive.</p>
        </div>
        <div className="step">
          <div className="step-head">
            <div className="step-icon">{STEP_ICONS.assign}</div>
            <div className="step-num">2</div>
          </div>
          <h4>Assign &amp; schedule</h4>
          <p>Pick languages, assign translators and reviewers per pair, set deadlines. Auto-handoff at each stage.</p>
        </div>
        <div className="step">
          <div className="step-head">
            <div className="step-icon">{STEP_ICONS.translate}</div>
            <div className="step-num">3</div>
          </div>
          <h4>Translate &amp; review</h4>
          <p>AI drafts in seconds. Translators polish in the editor. Reviewers approve segment-by-segment.</p>
        </div>
        <div className="step">
          <div className="step-head">
            <div className="step-icon">{STEP_ICONS.export}</div>
            <div className="step-num">4</div>
          </div>
          <h4>Export &amp; deliver</h4>
          <p>Push translated files back where they came from with formatting intact. Or hold them as the source of truth.</p>
        </div>
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section className="section-tight">
    <div className="rs-container">
      <div className="stats reveal stagger">
        <div className="stat"><div className="stat-num">100<span className="accent">+</span></div><div className="stat-label">Languages supported</div></div>
        <div className="stat"><div className="stat-num">3.2<span className="accent">×</span></div><div className="stat-label">Faster turnaround</div></div>
        <div className="stat"><div className="stat-num">62<span className="accent">%</span></div><div className="stat-label">Less rework</div></div>
        <div className="stat"><div className="stat-num">SOC 2</div><div className="stat-label">+ GDPR ready</div></div>
      </div>
    </div>
  </section>
);

const PRICING_PLANS = [
  { name: 'Free', monthly: 0, description: 'For solo translators and first projects.', features: ['1 Project', '5,000 Words / mo', 'Translation memory', 'Community support'], cta: 'Start free', highlight: false },
  { name: 'Essential', monthly: 80, description: 'Perfect for small teams and startups.', features: ['20 Projects', '50,000 Words / mo', '1 App Integration', 'Email Support'], cta: 'Get Started', highlight: false },
  { name: 'Growth', monthly: 250, description: 'For growing companies with global reach.', features: ['200 Projects', '500,000 Words / mo', '5 App Integrations', 'Priority Support'], cta: 'Upgrade Now', highlight: true },
  { name: 'Scale', monthly: 500, description: 'Advanced features for large scale operations.', features: ['Unlimited Projects', 'Unlimited Words', 'Unlimited Apps', 'Dedicated Support'], cta: 'Contact Sales', highlight: false },
];

const Pricing = ({ billing, setBilling }) => {
  const yearly = billing === 'yearly';
  return (
    <section className="section" id="pricing">
      <div className="rs-container">
        <div className="section-head reveal">
          <div className="eyebrow">Pricing</div>
          <h2>Choose your plan.</h2>
          <p className="section-lead">Choose the right program for your business. Switch any time.</p>
        </div>

        <div className="billing-toggle" role="tablist" aria-label="Billing period">
          <button role="tab" aria-selected={!yearly} className={`billing-btn ${!yearly ? 'is-active' : ''}`} onClick={() => setBilling('monthly')}>Monthly</button>
          <button role="tab" aria-selected={yearly} className={`billing-btn ${yearly ? 'is-active' : ''}`} onClick={() => setBilling('yearly')}>
            Yearly <span className="billing-save">2 months free</span>
          </button>
        </div>

        <div className="pricing stagger">
          {PRICING_PLANS.map((plan) => {
            const isFree = plan.monthly === 0;
            const price = isFree ? 0 : (yearly ? plan.monthly * 10 : plan.monthly);
            const period = isFree ? 'forever' : (yearly ? '/ year' : '/ month');
            return (
              <div key={plan.name} className={`price-card ${plan.highlight ? 'featured' : ''}`}>
                {plan.highlight && <span className="price-tag">Most Popular</span>}
                <div className="price-name">{plan.name}</div>
                <div className="price-amount">
                  <span className="price-currency">£</span>
                  <span className="num">{price.toLocaleString()}</span>
                  <span className="per">{period}</span>
                </div>
                <div className="price-sub">{plan.description}</div>
                <ul className="price-features">
                  {plan.features.map((f) => (
                    <li key={f} className="price-feature">
                      <svg className="price-check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button className={`btn ${plan.highlight ? 'btn-accent' : 'btn-ghost'}`} style={{ marginTop: 'auto' }}>{plan.cta}</button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => (
  <section className="section">
    <div className="rs-container">
      <div className="final-cta reveal reveal-scale">
        <h2>Stop herding files. Start shipping languages.</h2>
        <p>Join the waitlist for early-access pricing and a 1:1 onboarding call with our team.</p>
        <form onSubmit={(e) => e.preventDefault()} style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <input className="input input-lg" placeholder="work@yourcompany.com" style={{ width: 300 }} />
          <button className="btn btn-accent btn-lg" type="submit">Join the waitlist →</button>
        </form>
      </div>
    </div>
  </section>
);

const RushLandingPage = () => {
  const [billing, setBilling] = useState('monthly');
  useRushReveal();

  return (
    <div className="rs">
      <RushNav />
      <section className="hero">
        <div className="hero-mesh"></div>
        <div className="rs-container hero-inner hero-anim">
          <span className="hero-eyebrow"><span className="dot"></span>Now in early access — join the waitlist</span>
          <h1>The translation dashboard your team <em>actually wants to open.</em></h1>
          <p className="hero-sub">
            Rush Studio brings projects, AI + human translation, memory, glossaries, and deadlines into one calm workspace. Translate, collaborate, and deliver in 100+ languages — without the tab graveyard.
          </p>
          <form onSubmit={(e) => e.preventDefault()} className="hero-cta-row">
            <input className="input input-lg" placeholder="work@yourcompany.com" style={{ width: 280 }} />
            <button className="btn btn-accent btn-lg" type="submit">Join the waitlist →</button>
          </form>
          <div className="hero-meta">
            <span className="hero-meta-item">100+ languages</span>
            <span className="hero-meta-item">SOC 2 + GDPR</span>
            <span className="hero-meta-item">Drive · Dropbox · OneDrive</span>
            <span className="hero-meta-item">No credit card</span>
          </div>

          <div className="hero-product reveal reveal-scale">
            <div className="product-window">
              <div className="product-chrome">
                <div className="traffic"><span /><span /><span /></div>
                <div className="url">rush.studio/dashboard</div>
              </div>
              <DashboardHero />
            </div>
          </div>
        </div>
      </section>

      <LogoCloud />
      <Bento />
      <FeatureRows />
      <Differentiators />
      <HowItWorks />
      <Stats />
      <Pricing billing={billing} setBilling={setBilling} />
      <FinalCTA />
      <RushFooter />
    </div>
  );
};

export default RushLandingPage;
